import { Component, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { ManageEquipmentService } from '../../manage-equipment.service';
import { CustomerServiceIdentity } from '../../../IDENTITY_WEB/services/customer.service';
import { NotificationsService } from 'angular2-notifications';
import { PreferencesService } from '../../../preferences/preferences.service';
import { ScreenCode } from '../../../../shared/enums/allEnums';
import { GridColumn } from '../../../preferences/models/preferences.model';
import { InspectionComponentGroupVM, InspectionHistoryComponentMetadataVM, InspectionHistoryComponentVM, InspectionHistoryTabularResponseVM, InspectionHistoryTabularVM } from '../../models/manage-equipment.model';
import { ColumnPreferencesDialogComponent } from '../../../../shared/components/grid-column-preferences/column-preferences-dialog.component';
import { CryptoService } from '../../../../services/crypto.service';

interface StaticColumnGroup {
  title: string;
  isGroup: boolean;
  columns: GridColumn[];
}

@Component({
  selector: 'app-inspection-history-tabular',
  templateUrl: './inspection-history-tabular.component.html',
  styleUrls: ['./inspection-history-tabular.component.scss']
})

export class InspectionHistoryTabularComponent implements OnInit {
  @Input() equipmentId: number;
  isPopup: boolean = false;
  DashboardPrefernceList: GridColumn[] = [];
  dashboardPreferneceView: number[] = [];
  InspectionHistoryTabular: InspectionHistoryTabularVM[] = [];
  InspectionHistoryComponents: InspectionHistoryComponentVM[] = [];
  InspectionHistoryComponentMetadata:
    InspectionHistoryComponentMetadataVM[] = [];
  componentGroups: InspectionComponentGroupVM[] = [];
  componentValueMap = new Map<
    string,
    {
      wornPercentage: string;
      evalColor: string;
    }
  >();
  recordsPerPage: number = 100;
  pageNumber: number = 0;
  totalResults: number = 0;
  isBasicUser: number = 1;
  isLoading: boolean = false;
  tableScrollWidth = 0;
  readonly staticColumnKeys: string[] = [
    'Jobsite',
    'UnitNo',
    'InspectionDate',
    'DaysSince',
    'SMUAtInspection',
    'DiffFromPrevSMU',
    'FWDDistance',
    'ReverseDistance',
    'TotalDistance',
    'FwdDistancePctFromLastInspection',
    'FwdDistancePctFromComponentsInstalled',
    'DiffFromLastDist',
    'InspectedBy',
    'InspectType',
    'FrameSerialLeft',
    'FrameSerialRight',
    'HrsOnFrameLeft',
    'HrsOnFrameRight',
    'DistanceOnFrameLeft',
    'DistanceOnFrameRight',
    'TrackTensionLeft',
    'TrackTensionRight',
    'IceLugHeightLeft',
    'IceLugHeightRight'
    //,'GrouserbarLeft',
    //'GrouserbarRight'
  ];
  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: any,
    private _equipmentService: ManageEquipmentService,
    private _dialog: MatDialog,
    private _customerService: CustomerServiceIdentity,
    private readonly _pref: PreferencesService,
    private _crypto: CryptoService,
    public dialogRef: MatDialogRef<InspectionHistoryTabularComponent>
  ) { }

  ngOnInit(): void {
    if (this._data?.equipmentId != null) {
      this.isPopup = this._data.IsPopup;
    }
    this._customerService.CheckUCUserType().subscribe(result => {
      this.isBasicUser = result;
    });
    this.GetGridColumnsWithUserPreference();
    this.loadHistory();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  GetGridColumnsWithUserPreference(): void {
    this._pref.GetGridColumnsWithUserPreferenceAsync(ScreenCode.TabularInspectionHx).subscribe({
      next: (res: GridColumn[]) => {
        this.DashboardPrefernceList = res || [];
        this.dashboardPreferneceView =
          this.DashboardPrefernceList
            .filter(c => c.IsSelected)
            .map(c => c.GridId);
      },
      error: err => {
        console.error(
          'Error loading grid preferences:',
          err
        );
        this.DashboardPrefernceList = [];
        this.dashboardPreferneceView = [];
      }
    });
  }

  get orderedStaticColumns(): GridColumn[] {
    if (!this.DashboardPrefernceList?.length) {
      return [];
    }
    const selectedColumns = this.DashboardPrefernceList.filter(column => column.IsSelected && this.staticColumnKeys.includes(column.ColumnKey));
    return selectedColumns.sort((a, b) => {
      const indexA =
        this.dashboardPreferneceView.indexOf(a.GridId);
      const indexB =
        this.dashboardPreferneceView.indexOf(b.GridId);
      const safeIndexA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
      const safeIndexB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
      return safeIndexA - safeIndexB;
    });
  }

  isColumnSelected(columnKey: string): boolean {
    const column =
      this.DashboardPrefernceList?.find(
        c => c.ColumnKey === columnKey
      );
    return column
      ? this.dashboardPreferneceView.includes(column.GridId)
      : false;
  }
  
  openColumnDialog(): void {
    const dialogRef = this._dialog.open(
      ColumnPreferencesDialogComponent,
      {
        disableClose: true,
        width: '40%',
        height: '95%',
        data: {
          DashboardPrefernceList: this.DashboardPrefernceList,
          ScreenCode: ScreenCode.TabularInspectionHx
        }
      }
    );

    dialogRef.componentInstance.orderChanged
      .subscribe(
        (columns: GridColumn[]) => {
          this.DashboardPrefernceList =
            columns;
          this.dashboardPreferneceView =
            this.DashboardPrefernceList
              .filter(c => c.IsSelected)
              .map(c => c.GridId);
          this.buildComponentGroups();
        }
      );

    dialogRef.afterClosed()
      .subscribe((columns: GridColumn[]) => {
        if (!columns) {
          return;
        }
        this.DashboardPrefernceList = columns;
        this.dashboardPreferneceView =
          this.DashboardPrefernceList
            .filter(c => c.IsSelected)
            .sort((a, b) =>
              a.SortOrder - b.SortOrder
            )
            .map(c => c.GridId);
        this.buildComponentGroups();
      });
  }

  getColumnTitle(column: GridColumn): string {
    return column?.ColumnDisplayName ||
      column?.ColumnKey ||
      '';
  }

  getColumnHeaderClass(columnKey: string): string {
    switch (columnKey) {
      case 'Jobsite':
      case 'UnitNo':
      case 'InspectionDate':
      case 'InspectedBy':
      case 'InspectType':
        return 'text-left text-padding';
      case 'DaysSince':
      case 'SMUAtInspection':
      case 'DiffFromPrevSMU':
      case 'FWDDistance':
      case 'ReverseDistance':
      case 'TotalDistance':
      case 'FwdDistancePctFromLastInspection':
      case 'FwdDistancePctFromComponentsInstalled':
      case 'DiffFromLastDist':
      case 'HrsOnFrameLeft':
      case 'HrsOnFrameRight':
      case 'DistanceOnFrameLeft':
      case 'DistanceOnFrameRight':
      case 'TrackTensionLeft':
      case 'TrackTensionRight':
      case 'IceLugHeightLeft':
      case 'IceLugHeightRight':
      case 'GrouserbarLeft':
      case 'GrouserbarRight':
      case 'FrameSerialLeft':
      case 'FrameSerialRight':
        return 'text-center text-padding';
      default:
        return 'text-center text-padding';
    }
  }

  getColumnCellClass(columnKey: string): string {
    switch (columnKey) {
      case 'Jobsite':
      case 'UnitNo':
      case 'InspectionDate':
      case 'InspectedBy':
      case 'InspectType':
      case 'FrameSerialLeft':
      case 'FrameSerialRight':
        return 'text-left text-padding';
      default:
        return 'text-center text-padding';
    }
  }

  loadHistory(): void {
    this.isLoading = true;
    const id =
      this.equipmentId ??
      this._data?.equipmentId;
    this._equipmentService
      .getInspectionHistoryTabular(id)
      .subscribe({
        next:
          (
            response: InspectionHistoryTabularResponseVM
          ) => {
            console.log(
              'Inspection History Response:',
              response
            );
            this.InspectionHistoryTabular =
              response?.InspectionHistory || [];
            this.InspectionHistoryComponents =
              response?.Components || [];
            this.InspectionHistoryComponentMetadata =
              response?.ComponentMetadata || [];
            this.buildComponentValueMap();
            this.buildComponentGroups();
            this.totalResults =
              this.InspectionHistoryTabular.length;
            this.isLoading = false;
          },
        error: err => {
          console.error(
            'Error loading inspection history:',
            err
          );
          this.InspectionHistoryTabular = [];
          this.InspectionHistoryComponents = [];
          this.InspectionHistoryComponentMetadata = [];
          this.componentGroups = [];
          this.componentValueMap.clear();
          this.isLoading = false;
        }
      });
  }

  onPageEvent(event: any): void {
    this.pageNumber =
      event.pageIndex;
    this.recordsPerPage =
      event.pageSize;
    this.loadHistory();
  }
  changePageSize(size: number): void {
    this.recordsPerPage = size;
    this.pageNumber = 0;
    this.loadHistory();
  }

  private buildComponentValueMap(): void {
    this.componentValueMap.clear();
    for (
      const component
      of this.InspectionHistoryComponents
    ) {
      const key =
        this.getComponentMapKey(
          component.InspectionAuto,
          component.ComponentTypeId,
          component.Side,
          component.PositionNo
        );
      this.componentValueMap.set(
        key,
        {
          evalColor:
            component.EvalColor || '',
          wornPercentage:
            component.WornPercentage
        }
      );
    }
  }
  private getComponentMapKey(
    inspectionAuto: number,
    componentTypeId: number,
    side: string,
    positionNo: number
  ): string {
    return [
      inspectionAuto,
      componentTypeId,
      (side || '').toLowerCase(),
      positionNo
    ].join('_');
  }
  getComponentValue(
    inspectionAuto: number,
    component: InspectionHistoryComponentMetadataVM
  ): string | string {
    const key =
      this.getComponentMapKey(
        inspectionAuto,
        component.ComponentTypeId,
        component.Side,
        component.PositionNo
      );
    const value =
      this.componentValueMap.get(key);
    return value?.wornPercentage != null &&
      value.wornPercentage !== ''
      ? !isNaN(Number(value.wornPercentage))
        ? Number(value.wornPercentage) >= 0
          ? value.wornPercentage
          : '-'
        : value.wornPercentage
      : '-';

    //return value
    //  ? value.wornPercentage >= 0
    //    ? value.wornPercentage
    //    : '-'
    //  : '-';
  }

  private buildComponentGroups(): void {
    const groups:
      InspectionComponentGroupVM[] = [];
   
    for (
      const component
      of this.InspectionHistoryComponentMetadata
    ) {
      let group =
        groups.find(
          x =>
            x.ComponentTypeId ===
            component.ComponentTypeId
        );
      if (!group) {
        group = {
          ComponentTypeId:
            component.ComponentTypeId,
          ComponentName:
            component.ComponentName,
          Components: []
        };
        groups.push(group);
      }
      group.Components.push(component);
    }
 
    groups.forEach(group => {
      group.Components.sort((a, b) => {
        const positionA =
          Number(a.PositionNo) || 0;
        const positionB =
          Number(b.PositionNo) || 0;
        if (positionA !== positionB) {
          return positionA - positionB;
        }
        const sideA =
          (a.Side || '')
            .toString()
            .toLowerCase();
        const sideB =
          (b.Side || '')
            .toString()
            .toLowerCase();
        const sideOrder:
          { [key: string]: number } = {
          left: 1,
          right: 2
        };
        return (
          (sideOrder[sideA] || 99) -
          (sideOrder[sideB] || 99)
        );
      });
    });
 
    groups.sort((a, b) => {
      const sortOrderA =
        Number(
          a.Components.length
            ? a.Components[0].SortOrder
            : 0
        ) || 0;
      const sortOrderB =
        Number(
          b.Components.length
            ? b.Components[0].SortOrder
            : 0
        ) || 0;
      if (sortOrderA !== sortOrderB) {
        return sortOrderA - sortOrderB;
      }
      return (
        (Number(a.ComponentTypeId) || 0) -
        (Number(b.ComponentTypeId) || 0)
      );
    });
    this.componentGroups = groups;
    console.log(
      'componentGroups:',
      this.componentGroups
    );
  }
  //private buildComponentGroups(): void {

  //  /*
  //   * =========================================================
  //   * 1. BUILD REAL DYNAMIC COMPONENT GROUPS FROM DB
  //   * =========================================================
  //   */

  //  const groups: InspectionComponentGroupVM[] = [];

  //  for (
  //    const component
  //    of this.InspectionHistoryComponentMetadata
  //  ) {

  //    /*
  //     * ---------------------------------------------------------
  //     * Grouser Bar is NOT a DB component.
  //     *
  //     * It is hardcoded below.
  //     *
  //     * If SP/API ever sends ComponentTypeId = 231,
  //     * ignore it so we don't get duplicate Grouser Bar columns.
  //     * ---------------------------------------------------------
  //     */

  //    if (
  //      Number(component.ComponentTypeId) ===
  //      this.GROUSER_BAR_COMPONENT_TYPE_ID
  //    ) {
  //      continue;
  //    }

  //    /*
  //     * ---------------------------------------------------------
  //     * Find existing group by ComponentTypeId
  //     * ---------------------------------------------------------
  //     */

  //    let group =
  //      groups.find(
  //        x =>
  //          Number(x.ComponentTypeId) ===
  //          Number(component.ComponentTypeId)
  //      );

  //    /*
  //     * ---------------------------------------------------------
  //     * Create new component group
  //     * ---------------------------------------------------------
  //     */

  //    if (!group) {

  //      group = {
  //        ComponentTypeId:
  //          component.ComponentTypeId,

  //        ComponentName:
  //          component.ComponentName,

  //        Components: []
  //      };

  //      groups.push(group);
  //    }

  //    /*
  //     * ---------------------------------------------------------
  //     * Add component to its group
  //     * ---------------------------------------------------------
  //     */

  //    group.Components.push(component);
  //  }


  //  /*
  //   * =========================================================
  //   * 2. SORT COMPONENTS INSIDE EACH GROUP
  //   *
  //   * Order:
  //   *
  //   * Position
  //   *   ↓
  //   * Left
  //   *   ↓
  //   * Right
  //   *
  //   * This only controls columns INSIDE a component group.
  //   * =========================================================
  //   */

  //  groups.forEach(group => {

  //    group.Components.sort((a, b) => {

  //      /*
  //       * -------------------------------------------------------
  //       * Position order
  //       * -------------------------------------------------------
  //       */

  //      const positionA =
  //        Number(a.PositionNo) || 0;

  //      const positionB =
  //        Number(b.PositionNo) || 0;

  //      if (
  //        positionA !== positionB
  //      ) {
  //        return (
  //          positionA -
  //          positionB
  //        );
  //      }

  //      /*
  //       * -------------------------------------------------------
  //       * Side order
  //       *
  //       * Left before Right
  //       * -------------------------------------------------------
  //       */

  //      const sideA =
  //        (a.Side || '')
  //          .toString()
  //          .trim()
  //          .toLowerCase();

  //      const sideB =
  //        (b.Side || '')
  //          .toString()
  //          .trim()
  //          .toLowerCase();

  //      const sideOrder: {
  //        [key: string]: number;
  //      } = {
  //        left: 1,
  //        l: 1,
  //        right: 2,
  //        r: 2
  //      };

  //      return (
  //        (sideOrder[sideA] || 99) -
  //        (sideOrder[sideB] || 99)
  //      );
  //    });
  //  });


  //  /*
  //   * =========================================================
  //   * 3. CREATE HARDCODED GROUSER BAR GROUP
  //   *
  //   * ComponentTypeId = 231
  //   *
  //   * This component does NOT exist in DB.
  //   *
  //   * It is ONLY for rendering the UI/CSV headers.
  //   *
  //   * Columns:
  //   *
  //   *   Grouser Bar - L
  //   *   Grouser Bar - R
  //   *
  //   * There is NO data/value for either column.
  //   * =========================================================
  //   */

  //  const grouserBarGroup:
  //    InspectionComponentGroupVM = {

  //    ComponentTypeId:
  //      this.GROUSER_BAR_COMPONENT_TYPE_ID,

  //    ComponentName:
  //      'Grouser Bar',

  //    Components:
  //      [
  //        ...this.grouserBarMetadata
  //      ]
  //  };


  //  /*
  //   * =========================================================
  //   * 4. ADD GROUSER BAR TO DYNAMIC COMPONENT GROUPS
  //   * =========================================================
  //   */

  //  groups.push(
  //    grouserBarGroup
  //  );


  //  /*
  //   * =========================================================
  //   * 5. SORT ALL COMPONENT GROUPS BY COMPONENT TYPE ID
  //   *
  //   * IMPORTANT:
  //   *
  //   * DO NOT use SortOrder here.
  //   *
  //   * ComponentTypeId is now the source of truth for
  //   * dynamic component positioning.
  //   *
  //   * Example:
  //   *
  //   * 228 = Idler
  //   * 229 = Sprocket
  //   * 230 = Link Bush
  //   * 231 = Grouser Bar
  //   * 232 = Shoe
  //   *
  //   * Result:
  //   *
  //   * Idler
  //   * Sprocket
  //   * Link Bush
  //   * Grouser Bar
  //   * Shoe
  //   * =========================================================
  //   */

  //  groups.sort(
  //    (a, b) => {

  //      const componentTypeIdA =
  //        Number(a.ComponentTypeId) || 0;

  //      const componentTypeIdB =
  //        Number(b.ComponentTypeId) || 0;

  //      return (
  //        componentTypeIdA -
  //        componentTypeIdB
  //      );
  //    }
  //  );


  //  /*
  //   * =========================================================
  //   * 6. SAVE FINAL COMPONENT GROUPS
  //   * =========================================================
  //   */

  //  this.componentGroups =
  //    groups;


  //  /*
  //   * =========================================================
  //   * DEBUG
  //   * =========================================================
  //   */

  //  console.log(
  //    'Final componentGroups:',
  //    this.componentGroups.map(
  //      group => ({
  //        ComponentTypeId:
  //          group.ComponentTypeId,

  //        ComponentName:
  //          group.ComponentName,

  //        Components:
  //          group.Components.map(
  //            component => ({
  //              Side:
  //                component.Side,

  //              PositionNo:
  //                component.PositionNo,

  //              DisplayName:
  //                component.DisplayName
  //            })
  //          )
  //      })
  //    )
  //  );
  //}


  getOrderedComponents(group: any): any[] {
    if (
      !group ||
      !group.Components ||
      group.Components.length === 0
    ) {
      return [];
    }

    return [...group.Components].sort(
      (a: any, b: any) => {

        const positionA =
          Number(a.PositionNo) || 0;

        const positionB =
          Number(b.PositionNo) || 0;

        if (positionA !== positionB) {
          return positionA - positionB;
        }

        const sideA =
          (a.Side || '')
            .toString()
            .toLowerCase();

        const sideB =
          (b.Side || '')
            .toString()
            .toLowerCase();

        const sideOrder: {
          [key: string]: number;
        } = {
          left: 1,
          l: 1,
          right: 2,
          r: 2,
          c: 3,
          ol: 4,
          or: 5
        };

        return (
          (sideOrder[sideA] || 99) -
          (sideOrder[sideB] || 99)
        );
      }
    );
  }


  isPositionBasedGroup(group: any): boolean {
    if (
      !group ||
      !group.Components ||
      group.Components.length === 0
    ) {
      return false;
    }

    const positions: number[] =
      group.Components
        .map(
          (x: any): number =>
            Number(x.PositionNo)
        )
        .filter(
          (x: number): boolean =>
            !isNaN(x)
        );

    const uniquePositions: number[] =
      Array.from(
        new Set<number>(positions)
      );

    return uniquePositions.length > 1;
  }


  getUniquePositions(group: any): number[] {
    if (
      !group ||
      !group.Components ||
      group.Components.length === 0
    ) {
      return [];
    }

    const positions: number[] =
      group.Components
        .map(
          (x: any): number =>
            Number(x.PositionNo)
        )
        .filter(
          (x: number): boolean =>
            !isNaN(x)
        );

    const uniquePositions: number[] =
      Array.from(
        new Set<number>(positions)
      );

    return uniquePositions.sort(
      (a, b) => a - b
    );
  }


  getComponentsByPosition(
    group: any,
    position: number
  ): any[] {

    if (
      !group ||
      !group.Components
    ) {
      return [];
    }

    return group.Components
      .filter(
        (x: any): boolean =>
          Number(x.PositionNo) ===
          Number(position)
      )
      .sort(
        (a: any, b: any): number => {

          const sideA =
            (a.Side || '')
              .toString()
              .toLowerCase();

          const sideB =
            (b.Side || '')
              .toString()
              .toLowerCase();

          const sideOrder: {
            [key: string]: number;
          } = {
            left: 1,
            l: 1,
            right: 2,
            r: 2,
            c: 3,
            ol: 4,
            or: 5
          };

          return (
            (sideOrder[sideA] || 99) -
            (sideOrder[sideB] || 99)
          );
        }
      );
  }


  formatPosition(
    position: number | null | undefined,
    group?: any
  ): string {

    const isAverage =
      group?.IsAverage === true ||
      group?.isAverage === true ||
      group?.IsAverage === 1 ||
      group?.isAverage === 1 ||
      group?.DataColumn
        ?.toLowerCase()
        ?.includes('avg') ||
      group?.DisplayName
        ?.toLowerCase()
        ?.includes('avg');

    if (isAverage) {
      return 'AVG';
    }

    if (
      position === null ||
      position === undefined ||
      Number.isNaN(Number(position))
    ) {
      return '';
    }

    return Number(position)
      .toString()
      .padStart(2, '0');
  }


  getPositionSideLabel(
    component: any,
    group: any
  ): string {

    if (!component) {
      return '';
    }

    const name =
      group?.ComponentName
        ?.toString()
        ?.toLowerCase();

    if (
      name === 'idler' ||
      name === 'sprocket'
    ) {

      if (
        Number(component.PositionNo) === 1
      ) {
        return 'F';
      }

      if (
        Number(component.PositionNo) === 2
      ) {
        return 'R';
      }
    }

    if (component.Side) {

      const side =
        component.Side
          .toString()
          .toLowerCase();

      if (side === 'left') {
        return 'L';
      }

      if (side === 'right') {
        return 'R';
      }

      if (side === 'l') {
        return 'L';
      }

      if (side === 'r') {
        return 'R';
      }

      return component.Side;
    }

    return component.PositionNo
      ? component.PositionNo.toString()
      : '';
  }


  getComponentEvalColor(
    inspectionAuto: number,
    component:
      InspectionHistoryComponentMetadataVM
  ): string {

    const key =
      this.getComponentMapKey(
        inspectionAuto,
        component.ComponentTypeId,
        component.Side,
        component.PositionNo
      );

    const value =
      this.componentValueMap.get(key);

    return value?.evalColor || '';
  }


  getTextColor(
    backgroundColor: string
  ): string {

    if (!backgroundColor) {
      return '#000000';
    }

    const hex =
      backgroundColor.replace('#', '');

    if (hex.length !== 6) {
      return '#000000';
    }

    const r =
      parseInt(
        hex.substring(0, 2),
        16
      );

    const g =
      parseInt(
        hex.substring(2, 4),
        16
      );

    const b =
      parseInt(
        hex.substring(4, 6),
        16
      );

    const brightness =
      (
        r * 299 +
        g * 587 +
        b * 114
      ) / 1000;

    return brightness < 128
      ? '#ffffff'
      : '#000000';
  }


  openInterpretation(id: number): void {
    window.open(
      '/interpretation/' + id,
      '_blank'
    );
  }


  get orderedStaticColumnGroups(): StaticColumnGroup[] {

    const columns =
      this.orderedStaticColumns;

    if (
      !columns ||
      columns.length === 0
    ) {
      return [];
    }

    const groups: StaticColumnGroup[] = [];

    let i = 0;

    while (i < columns.length) {

      const currentColumn =
        columns[i];

      const currentSide =
        this.getColumnSide(
          currentColumn
        );

      if (currentSide) {

        const baseTitle =
          this.getColumnBaseTitle(
            currentColumn.ColumnDisplayName
          );

        const groupColumns:
          GridColumn[] = [
            currentColumn
          ];

        if (
          i + 1 <
          columns.length
        ) {

          const nextColumn =
            columns[i + 1];

          const nextSide =
            this.getColumnSide(
              nextColumn
            );

          const nextBaseTitle =
            this.getColumnBaseTitle(
              nextColumn.ColumnDisplayName
            );

          if (
            nextSide &&
            nextBaseTitle
              .toLowerCase() ===
            baseTitle
              .toLowerCase()
          ) {

            groupColumns.push(
              nextColumn
            );

            i++;
          }
        }

        groups.push({
          title: baseTitle,
          isGroup:
            groupColumns.length > 1,
          columns: groupColumns
        });

      } else {

        groups.push({
          title:
            this.getColumnTitle(
              currentColumn
            ),
          isGroup: false,
          columns: [
            currentColumn
          ]
        });
      }

      i++;
    }

    return groups;
  }


  getColumnSide(
    column: GridColumn
  ): string {

    if (!column) {
      return '';
    }

    const title = (
      column.ColumnDisplayName ||
      column.ColumnKey ||
      ''
    )
      .toString()
      .trim();

    const match =
      title.match(
        /-\s*(L|R)\s*$/i
      );

    if (!match) {
      return '';
    }

    return match[1]
      .toUpperCase();
  }


  getColumnBaseTitle(
    title: string
  ): string {

    if (!title) {
      return '';
    }

    return title
      .toString()
      .replace(
        /\s*-\s*(L|R)\s*$/i,
        ''
      )
      .trim();
  }


  getStaticSideLabel(
    column: GridColumn
  ): string {

    const side =
      this.getColumnSide(
        column
      );

    if (side) {
      return side;
    }

    return '';
  }


  downloadCsv(): void {

    if (
      !this.InspectionHistoryTabular ||
      this.InspectionHistoryTabular.length === 0
    ) {
      return;
    }

    const headers: string[] = [];
    const rows: string[][] = [];

    /*
     * =========================================================
     * 1. STATIC COLUMN HEADERS
     * =========================================================
     */

    for (
      const column
      of this.orderedStaticColumns
    ) {

      headers.push(
        this.getColumnTitle(
          column
        )
      );
    }

    /*
     * =========================================================
     * 2. DYNAMIC COMPONENT HEADERS
     *
     * Grouser Bar now comes from SP metadata.
     * No hardcoded Grouser Bar logic is required.
     *
     * Example metadata:
     *
     * Grouser Bar | Left
     * Grouser Bar | Right
     *
     * will automatically produce:
     *
     * Grouser Bar - L
     * Grouser Bar - R
     * =========================================================
     */

    for (
      const group
      of this.componentGroups
    ) {

      const components =
        this.getOrderedComponents(
          group
        );

      for (
        const component
        of components
      ) {

        let header =
          group.ComponentName || '';

        /*
         * Position based component
         */

        if (
          this.isPositionBasedGroup(
            group
          )
        ) {

          const position =
            this.formatPosition(
              component.PositionNo,
              group
            );

          const side =
            this.getPositionSideLabel(
              component,
              group
            );

          header =
            `${header} - ${position} - ${side}`;

        }

        /*
         * Normal component including
         * Grouser Bar.
         *
         * Side comes directly from
         * SP metadata.
         */

        else {

          const side =
            (
              component.Side || ''
            )
              .toString()
              .toLowerCase();

          if (
            side === 'left' ||
            side === 'l'
          ) {

            header += ' - L';

          }
          else if (
            side === 'right' ||
            side === 'r'
          ) {

            header += ' - R';
          }
        }

        headers.push(header);
      }
    }

    /*
     * =========================================================
     * 3. BUILD DATA ROWS
     * =========================================================
     */

    for (
      const item
      of this.InspectionHistoryTabular
    ) {

      const row: string[] = [];

      /*
       * STATIC VALUES
       */

      for (
        const column
        of this.orderedStaticColumns
      ) {

        row.push(
          this.getCsvStaticValue(
            item,
            column.ColumnKey
          )
        );
      }

      /*
       * DYNAMIC COMPONENT VALUES
       *
       * Grouser Bar comes from metadata,
       * but there is no inspection value.
       *
       * Therefore it will naturally return
       * blank because there is no matching
       * component data in componentValueMap.
       */

      for (
        const group
        of this.componentGroups
      ) {

        const components =
          this.getOrderedComponents(
            group
          );

        for (
          const component
          of components
        ) {

          const value =
            this.getComponentValue(
              item.InspectionAuto,
              component
            );

          row.push(
            value === null ||
              value === undefined
              ? ''
              : value.toString()
          );
        }
      }

      rows.push(row);
    }

    /*
     * =========================================================
     * 4. CREATE CSV
     * =========================================================
     */

    const csvRows: string[] = [];

    csvRows.push(
      headers
        .map(value =>
          this.escapeCsvValue(
            value
          )
        )
        .join(',')
    );

    for (
      const row
      of rows
    ) {

      csvRows.push(
        row
          .map(value =>
            this.escapeCsvValue(
              value
            )
          )
          .join(',')
      );
    }

    /*
     * =========================================================
     * 5. DOWNLOAD
     * =========================================================
     */

    const csvContent =
      '\uFEFF' +
      csvRows.join('\r\n');

    const blob =
      new Blob(
        [csvContent],
        {
          type:
            'text/csv;charset=utf-8;'
        }
      );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement('a');

    link.href = url;

    link.download =
      `Inspection_History_${this.getCsvDate()}.csv`;

    link.click();

    window.URL.revokeObjectURL(
      url
    );
  }


  private getCsvStaticValue(
    item: InspectionHistoryTabularVM,
    columnKey: string
  ): string {

    let value: any = '';

    switch (columnKey) {

      case 'Jobsite':
        value = item.Jobsite;
        break;

      case 'UnitNo':
        value = item.UnitNo;
        break;

      case 'InspectionDate':
        value = item.InspectionDate
          ? this.formatCsvDate(
            item.InspectionDate
          )
          : '';
        break;

      case 'DaysSince':
        value = item.DaysSince;
        break;

      case 'SMUAtInspection':
        value = item.SMUAtInspection;
        break;

      case 'DiffFromPrevSMU':
        value = item.DiffFromPrevSMU;
        break;

      case 'FWDDistance':
        value = item.FWDDistance;
        break;

      case 'ReverseDistance':
        value = item.ReverseDistance;
        break;

      case 'TotalDistance':
        value = item.TotalDistance;
        break;

      case 'FwdDistancePctFromLastInspection':
        value =
          item.FwdDistanceChangePercentage;
        break;

      case 'FwdDistancePctFromComponentsInstalled':
        value =
          item.FwdDistanceChangePercentageSinceComponentInstall;
        break;

      case 'DiffFromLastDist':
        value = item.DiffFromLastDist;
        break;

      case 'InspectedBy':
        value =
          this._crypto.decrypt(
            item.InspectedBy
          );
        break;

      case 'InspectType':
        value = item.InspectType;
        break;

      case 'FrameSerialLeft':
        value = item.FrameSerialLeft;
        break;

      case 'FrameSerialRight':
        value = item.FrameSerialRight;
        break;

      case 'HrsOnFrameLeft':
        value = item.HrsOnFrameLeft;
        break;

      case 'HrsOnFrameRight':
        value = item.HrsOnFrameRight;
        break;

      case 'DistanceOnFrameLeft':
        value = item.DistanceOnFrameLeft;
        break;

      case 'DistanceOnFrameRight':
        value = item.DistanceOnFrameRight;
        break;

      case 'TrackTensionLeft':
        value = item.TrackTensionLeft;
        break;

      case 'TrackTensionRight':
        value = item.TrackTensionRight;
        break;

      case 'IceLugHeightLeft':
        value = item.IceLugHeightLeft;
        break;

      case 'IceLugHeightRight':
        value = item.IceLugHeightRight;
        break;

      default:
        value =
          (item as any)?.[columnKey] ?? '';
        break;
    }

    return this.decryptCsvValue(
      value
    );
  }


  private escapeCsvValue(
    value: any
  ): string {

    if (
      value === null ||
      value === undefined
    ) {
      return '""';
    }

    const stringValue =
      value.toString();

    return `"${stringValue.replace(
      /"/g,
      '""'
    )}"`;
  }


  private formatCsvDate(
    value: any
  ): string {

    const date =
      new Date(value);

    if (
      isNaN(
        date.getTime()
      )
    ) {
      return value?.toString() || '';
    }

    const day =
      date.getDate()
        .toString()
        .padStart(2, '0');

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];

    const month =
      monthNames[
      date.getMonth()
      ];

    const year =
      date.getFullYear();

    return `${day}-${month}-${year}`;
  }


  private getCsvDate(): string {

    const now =
      new Date();

    const year =
      now.getFullYear();

    const month =
      (now.getMonth() + 1)
        .toString()
        .padStart(2, '0');

    const day =
      now.getDate()
        .toString()
        .padStart(2, '0');

    return `${year}-${month}-${day}`;
  }


  private decryptCsvValue(
    value: any
  ): string {

    if (
      value === null ||
      value === undefined
    ) {
      return '';
    }

    return value.toString();
  }


  isEqualiserBar(
    component: any,
    group?: any
  ): boolean {

    return (
      component?.ComponentName ===
      'EqualiserBar' ||
      component?.ComponentName ===
      'Equaliser Bar' ||
      group?.ComponentName ===
      'EqualiserBar' ||
      group?.ComponentName ===
      'Equaliser Bar'
    );
  }


  getEqualiserBarLabel(
    component: any
  ): string {

    switch (component?.Side) {

      case 'C':
        return 'C';

      case 'OL':
        return 'OL';

      case 'OR':
        return 'OR';

      default:
        return '';
    }
  }




}
